// Νέα κράτηση ή αλλαγή, από το ταμείο. Ίδια ροή με τη Sala: τηλέφωνο (βρίσκει τον
// πελάτη), άτομα, μέρα, ελεύθερες ώρες ανά βάρδια (ο server ξέρει κανόνες και τραπέζια),
// πρόταση τραπεζιού, σημειώσεις, μήνυμα στον πελάτη.

import React, { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Star, X } from 'lucide-react';
import { Btn, Field, Note, Segmented, Stepper, inputClass } from '../floorEditor/ui';
import { resApi } from './api';
import { addDays, fmt, toMin } from './model';
import { useK } from './useK';
import type { Availability, Guest, Notify, Reservation } from './types';

const TAGS = ['birthday', 'anniversary', 'business', 'highchair', 'wheelchair'] as const;
const digits = (s: string) => s.replace(/\D/g, '');
const uuid = () => (typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`);

export default function ReservationForm({ storeId, date: viewDate, today, editing, tableNo, onClose, onSaved }: {
  storeId: number; date: string; today: string; editing?: Reservation; tableNo: (id: number) => string;
  onClose: () => void; onSaved: (r: Reservation | null) => void;
}) {
  const { k, dayLabel, errText } = useK();
  const [phone, setPhone] = useState(editing?.customer_phone || '');
  const [name, setName] = useState(editing?.customer_name || '');
  const [email, setEmail] = useState(editing?.customer_email || '');
  const [guest, setGuest] = useState<Guest | null>(null);
  const [found, setFound] = useState<Guest[]>([]);
  const [party, setParty] = useState(editing?.party_size || 2);
  const [date, setDate] = useState(editing?.reservation_date || (viewDate < today ? today : viewDate));
  const [time, setTime] = useState<number | null>(editing ? toMin(editing.reservation_time) : null);
  const [tableIds, setTableIds] = useState<number[] | null>(null);
  const [tags, setTags] = useState<string[]>(editing?.tags || []);
  const [notes, setNotes] = useState(editing?.notes || '');
  const [notify, setNotify] = useState<Notify>('default');
  const [avail, setAvail] = useState<Availability | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [uid] = useState(uuid);

  // Πελάτης: από 3 ψηφία τηλεφώνου ή 3 γράμματα ονόματος (μόνο σε νέα κράτηση).
  const q = guest || editing ? '' : digits(phone).length >= 3 ? phone : name.trim().length >= 3 ? name : '';
  useEffect(() => {
    if (!q) { setFound([]); return; }
    let alive = true;
    const id = window.setTimeout(() => {
      resApi.guests(storeId, q).then(g => { if (alive) setFound(g.slice(0, 5)); }).catch(() => { if (alive) setFound([]); });
    }, 250);
    return () => { alive = false; window.clearTimeout(id); };
  }, [q, storeId]);

  useEffect(() => {
    let alive = true;
    setAvail(null);
    resApi.availability(storeId, date, party, editing?.id).then(a => { if (alive) setAvail(a); }).catch(() => { if (alive) setAvail(null); });
    return () => { alive = false; };
  }, [storeId, date, party, editing?.id]);

  const slot = useMemo(() => avail?.shifts.flatMap(s => s.slots).find(s => s.minutes === time), [avail, time]);
  const options = slot?.options || [];
  // Το τραπέζι που διάλεξες (ή που είχε ήδη η κράτηση) μένει όσο είναι ελεύθερο στη μέρα/ώρα/παρέα. Αλλιώς η πρόταση.
  const own = editing ? [editing.table_id, ...(editing.extra_table_ids || [])].filter((x): x is number => x != null) : [];
  const wanted = tableIds ?? own;
  const atOwnSlot = !!editing && !tableIds && time === toMin(editing.reservation_time) && date === editing.reservation_date && party === editing.party_size;
  const chosen = wanted.length > 0 && (atOwnSlot || options.some(o => o.table_ids.join() === wanted.join()))
    ? wanted
    : options[0]?.table_ids || [];
  const days = Array.from({ length: 7 }, (_, i) => addDays(today, i));

  const pickGuest = (g: Guest) => { setGuest(g); setPhone(g.phone); setName(g.name); setEmail(g.email || ''); setFound([]); };

  const submit = async () => {
    setError(null);
    if (!name.trim()) return setError(k('form.errName'));
    if (phone && digits(phone).length < 10) return setError(k('form.errPhone'));
    if (time == null) return setError(k('form.errTime'));
    const body = {
      customer_name: name.trim(),
      customer_phone: phone.trim() || undefined,
      customer_email: email.trim() || undefined,
      reservation_date: date,
      reservation_time: fmt(time),
      party_size: party,
      table_ids: chosen,
      tags,
      notes: notes.trim() || undefined,
    };
    setBusy(true);
    try {
      const saved = editing
        ? await resApi.update(storeId, editing.id, body)
        : await resApi.create(storeId, { ...body, customer_id: guest?.id, notify, source: 'phone', status: 'confirmed', client_uuid: uid });
      onSaved(saved);
    } catch (e) {
      setError(errText(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex justify-end bg-black/40" onClick={onClose}>
      <div className="flex h-full w-full max-w-[620px] flex-col border-l border-line bg-surface-1 shadow-xl" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true"
        aria-label={editing ? k('form.editTitle') : k('form.newTitle')}>
        <div className="flex items-start gap-3 border-b border-line px-5 py-4">
          <div className="min-w-0 flex-1">
            <h3 className="text-[20px] font-bold text-t1">{editing ? k('form.editTitle') : k('form.newTitle')}</h3>
            {editing && <p className="text-[13px] text-t3">{editing.customer_name}</p>}
          </div>
          <Btn size="icon" aria-label={k('close')} onClick={onClose}><X className="h-4 w-4" /></Btn>
        </div>

        <div className="grid min-h-0 flex-1 auto-rows-max content-start gap-4 overflow-y-auto px-5 py-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label={k('form.phone')}>
              <input className={inputClass} type="tel" inputMode="tel" value={phone} autoFocus={!editing}
                onChange={e => { setPhone(e.target.value); if (guest) setGuest(null); }} />
            </Field>
            <Field label={k('form.name')}>
              <input className={inputClass} value={name} onChange={e => { setName(e.target.value); if (guest) setGuest(null); }} />
            </Field>
          </div>
          {guest ? (
            <div className="grid gap-1 rounded-lg border border-line bg-surface-2 px-3.5 py-2.5">
              <div className="flex items-center justify-between gap-2">
                <span className="flex items-center gap-1.5 text-[14px] font-semibold text-t1">{guest.name}{guest.is_vip && <Star className="h-4 w-4 fill-amber-400 text-amber-500" />}</span>
                <button type="button" onClick={() => setGuest(null)} className="text-[12.5px] text-t3 hover:text-t1">{k('form.otherGuest')}</button>
              </div>
              <span className="text-[13px] text-t2">{k('detail.visits', { count: guest.visits_count })}{guest.no_show_count > 0 && ` · ${k('detail.noShows', { count: guest.no_show_count })}`}</span>
              {guest.allergies && <span className="flex items-center gap-1.5 text-[13px] font-medium text-red-600"><AlertTriangle className="h-4 w-4" />{guest.allergies}</span>}
              {guest.notes && <span className="text-[13px] text-t3">{guest.notes}</span>}
            </div>
          ) : found.length > 0 && (
            <ul className="grid overflow-hidden rounded-lg border border-line">
              {found.map(g => (
                <li key={g.id}>
                  <button type="button" onClick={() => pickGuest(g)} className="flex w-full items-center justify-between gap-2 border-b border-line px-3 py-2 text-left last:border-b-0 hover:bg-surface-2">
                    <span className="flex items-center gap-1.5 text-[14px] text-t1">{g.name}{g.is_vip && <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-500" />}</span>
                    <span className="text-[12.5px] text-t3">{g.phone} · {k('detail.visits', { count: g.visits_count })}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          <Field label={k('form.email')}><input className={inputClass} type="email" value={email} onChange={e => setEmail(e.target.value)} /></Field>

          <div className="flex flex-wrap items-center justify-between gap-3">
            <span className="text-[14px] font-medium text-t1">{k('form.party')}</span>
            <Stepper value={party} display={k('people', { count: party })} min={1} max={60} onChange={setParty} less={k('form.less')} more={k('form.more')} />
          </div>

          <div className="grid gap-2">
            <span className="text-[13px] font-medium text-t2">{k('form.day')}</span>
            <div className="flex flex-wrap gap-1.5">
              {days.map(d => (
                <button key={d} type="button" aria-pressed={date === d} onClick={() => { setDate(d); setTime(null); }}
                  className={`h-9 rounded-lg border px-3 text-[13px] font-medium ${date === d ? 'border-blue-600 bg-blue-600 text-white' : 'border-line bg-surface-1 text-t2 hover:border-line-strong'}`}>
                  {dayLabel(d, today)}
                </button>
              ))}
              <input type="date" value={date} min={today} onChange={e => { if (e.target.value) { setDate(e.target.value); setTime(null); } }}
                className="h-9 rounded-lg border border-line bg-surface-2 px-2 text-[13px] text-t1" aria-label={k('day.pick')} />
            </div>
          </div>

          <div className="grid gap-2">
            <span className="text-[13px] font-medium text-t2">{k('form.time')}</span>
            {!avail ? <div className="h-20 animate-pulse rounded-lg bg-surface-2" /> : avail.shifts.length === 0 ? <Note tone="warn">{k('form.noShifts')}</Note> : avail.shifts.map(s => (
              <div key={s.id} className="grid gap-1.5">
                <span className="text-[12px] font-semibold uppercase tracking-wide text-t3">{s.name}{s.closed ? ` · ${k('form.closed')}` : ''}</span>
                {!s.closed && (
                  <div className="grid grid-cols-[repeat(auto-fill,minmax(72px,1fr))] gap-1.5">
                    {s.slots.map(sl => (
                      <button key={sl.minutes} type="button" disabled={!sl.available} aria-pressed={time === sl.minutes} title={sl.reason || undefined}
                        onClick={() => setTime(sl.minutes)}
                        className={`h-10 rounded-lg border text-[14px] font-semibold tabular-nums transition-colors disabled:cursor-not-allowed disabled:opacity-35 ${time === sl.minutes ? 'border-blue-600 bg-blue-600 text-white' : 'border-line bg-surface-1 text-t1 hover:border-blue-400'}`}>
                        {sl.time}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          {time != null && (
            <div className="grid gap-2">
              <span className="text-[13px] font-medium text-t2">{k('form.table')}</span>
              {options.length === 0 ? <Note tone="warn">{k('form.noTable')}</Note> : (
                <div className="flex flex-wrap gap-1.5">
                  {options.map((o, i) => {
                    const on = chosen.join() === o.table_ids.join();
                    return (
                      <button key={o.table_ids.join('+')} type="button" aria-pressed={on} onClick={() => setTableIds(o.table_ids)}
                        className={`h-10 rounded-lg border px-3 text-[14px] font-semibold ${on ? 'border-blue-600 bg-blue-600/10 text-blue-700 dark:text-blue-300' : 'border-line bg-surface-1 text-t1 hover:border-line-strong'}`}>
                        {k('tablePrefix')}{o.table_ids.map(tableNo).join('+')}
                        <span className="ml-1.5 text-[12px] font-normal text-t3">{k('seat.seats', { count: o.capacity })}{i === 0 ? ` · ${k('form.suggested')}` : ''}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          <div className="grid gap-2">
            <span className="text-[13px] font-medium text-t2">{k('form.notes')}</span>
            <div className="flex flex-wrap gap-1.5">
              {TAGS.map(t => {
                const label = k(`tags.${t}`);
                const on = tags.includes(label);
                return (
                  <button key={t} type="button" aria-pressed={on} onClick={() => setTags(on ? tags.filter(x => x !== label) : [...tags, label])}
                    className={`h-8 rounded-full border px-3 text-[12.5px] ${on ? 'border-blue-600 bg-blue-600/10 text-blue-700 dark:text-blue-300' : 'border-line text-t2 hover:border-line-strong'}`}>{label}</button>
                );
              })}
            </div>
            <textarea className={`${inputClass} h-20 py-2`} value={notes} onChange={e => setNotes(e.target.value)} placeholder={k('form.notesHint')} />
          </div>

          {!editing && (
            <div className="grid gap-1.5">
              <span className="text-[13px] font-medium text-t2">{k('form.notify')}</span>
              <Segmented<Notify> value={notify} onChange={setNotify}
                options={(['default', 'sms', 'email', 'both', 'none'] as Notify[]).map(v => ({ value: v, label: k(`notify.${v}`) }))} />
              {(notify === 'sms' || notify === 'both') && <span className="text-[12px] text-t3">{k('form.smsCost')}</span>}
            </div>
          )}
          {error && <Note tone="error">{error}</Note>}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-line px-5 py-3">
          <Btn onClick={onClose}>{k('form.cancel')}</Btn>
          <Btn variant="primary" disabled={busy} onClick={submit}>{busy ? k('form.saving') : editing ? k('form.save') : k('form.create')}</Btn>
        </div>
      </div>
    </div>
  );
}
